// src/layout/BranchPill.tsx
//
// The branch chip in the nav: "CS · Computer Science & IT ▾".
//
// Switching here reorders the user's branch list so the chosen one becomes
// primary — the same write Settings uses. That keeps "primary branch" a
// single concept rather than having a separate per-session selection that
// could disagree with Settings.

import { useEffect } from "react";
import { Link } from "react-router";
import { ChevronDown, Check, Plus, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useBranches, useBranchMutations } from "@/hooks/useBranches";
import { setProfile } from "@/lib/analytics";

export function BranchPill({ compact = false }: { compact?: boolean }) {
  const { branches, primary, isLoading, needsBranch } = useBranches();

  // The nav is the one place the primary branch is always known.
  useEffect(() => {
    if (primary) setProfile({ branch: primary.subject_code });
  }, [primary]);
  const { reorder } = useBranchMutations();

  // Hold the pill's footprint while loading so the nav doesn't reflow when
  // it resolves.
  if (isLoading) {
    return <Skeleton className="h-8 w-24 rounded-full sm:w-44" />;
  }

  // Users from before branches existed whose branch couldn't be inferred.
  // An invitation, not an error.
  if (needsBranch) {
    return (
      <Link
        to="/settings?section=branches"
        className="flex items-center gap-1.5 rounded-full border border-dashed border-hairline bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-teal hover:text-teal"
      >
        <Plus className="h-3 w-3" />
        Pick your branch
      </Link>
    );
  }

  if (!primary) return null;

  const makePrimary = (subjectId: string) => {
    if (subjectId === primary.subject_id) return;
    // Chosen branch first, everything else in its existing order.
    reorder.mutate([
      subjectId,
      ...branches
        .filter((b) => b.subject_id !== subjectId)
        .map((b) => b.subject_id),
    ]);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Current branch: ${primary.subject_name}. Change branch.`}
          className="flex max-w-[220px] items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-xs transition-colors hover:bg-accent"
        >
          <span className="font-mono text-[10px] text-muted-foreground">
            {primary.subject_code}
          </span>
          {/* The full name is the first thing to go when space is tight —
the code alone still identifies the branch. */}
          {!compact && (
            <span className="truncate font-semibold">
              {primary.subject_name}
            </span>
          )}
          {reorder.isPending ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64 rounded-xl p-1">
        <div className="px-2.5 py-2">
          <p className="eyebrow">Your branches</p>
        </div>
        {branches.map((b) => (
          <DropdownMenuItem
            key={b.subject_id}
            onClick={() => makePrimary(b.subject_id)}
            className="mx-0.5 flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm"
          >
            <span className="w-7 shrink-0 font-mono text-[10px] text-muted-foreground">
              {b.subject_code}
            </span>
            <span className="flex-1 truncate">{b.subject_name}</span>
            {b.is_primary && (
              <Check className="h-3.5 w-3.5 shrink-0 text-teal" />
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          asChild
          className="mx-0.5 rounded-lg px-2.5 py-2 text-sm"
        >
          <Link to="/settings?section=branches" className="cursor-pointer">
            <Plus className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
            Manage branches
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
